import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { userService } from '../services/user.service';
import { 
  FaFile, FaUpload, FaTrash, FaEye, FaFileAlt, 
  FaExclamationCircle, FaCheck, FaSync, FaPlus,
  FaShieldAlt, FaTimes, FaLock, FaEdit, FaUserShield, FaUser, FaSave,
  FaBrain, FaUsers, FaSearch, FaUnlock, FaKey, FaChartBar
} from 'react-icons/fa';
import AIModelManager from './AIModelManager';
import AIUsageStats from './AIUsageStats';
import QuestionsTab from './QuestionsTab';
import { getAvailableAIModels } from '../services/preferences.service';

// Clean, minimalist styles based on the business units table design
const PageContainer = styled.div`
  padding: 20px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const TabsContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #e8e8e8;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
`;

const Tab = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.active ? '#1890ff' : '#595959'};
  border-bottom: 2px solid ${props => props.active ? '#1890ff' : 'transparent'};
  transition: all 0.3s;
  flex: 0 0 auto;
  min-width: max-content;
  
  &:hover {
    color: #40a9ff;
  }
  
  svg {
    font-size: 16px;
    flex-shrink: 0;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 500;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #1890ff;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 200px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  padding-left: 36px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #8c8c8c;
  display: flex;
  align-items: center;
`;

const Card = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

const TableBody = styled.tbody`
  tr:hover {
    background-color: #fafafa;
  }
`;

const TableRow = styled.tr`
  background-color: ${props => props.highlighted ? '#e6f7ff' : 'white'};
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 500;
  color: #595959;
  font-size: 14px;
`;

const TableCell = styled.td`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
  color: #262626;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.danger ? '#ff4d4f' : '#1890ff'};
  
  &:hover {
    background-color: ${props => props.danger ? '#fff1f0' : '#e6f7ff'};
  }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 2px;
  font-size: 12px;
  background-color: ${props => props.role === 'admin' ? '#f6ffed' : '#e6f7ff'};
  color: ${props => props.role === 'admin' ? '#52c41a' : '#1890ff'};
  border: 1px solid ${props => props.role === 'admin' ? '#b7eb8f' : '#91d5ff'};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8c8c8c;
  
  .spin {
    animation: spin 1s linear infinite;
    margin-right: 8px;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #8c8c8c;
`;

const EmptyStateText = styled.p`
  margin-top: 16px;
  font-size: 14px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #8c8c8c;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 3px;
  background: ${props => props.primary ? '#1890ff' : '#f5f5f5'};
  color: ${props => props.primary ? '#fff' : '#595959'};
  border: 1px solid ${props => props.primary ? '#1890ff' : '#d9d9d9'};
  font-weight: 400;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: ${props => props.primary ? '#40a9ff' : '#fafafa'};
    border-color: ${props => props.primary ? '#40a9ff' : '#d9d9d9'};
  }
  
  &:disabled {
    background: #f5f5f5;
    border-color: #d9d9d9;
    color: #bfbfbf;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 12px 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  background-color: ${p => {
    switch(p.type) {
      case 'error': return '#fff2f0';
      case 'warning': return '#fffbe6';
      default: return '#f6ffed';
    }
  }};
  border: 1px solid ${p => {
    switch(p.type) {
      case 'error': return '#ffccc7';
      case 'warning': return '#ffe58f';
      default: return '#b7eb8f';
    }
  }};
  color: ${p => {
    switch(p.type) {
      case 'error': return '#cf1322';
      case 'warning': return '#d48806';
      default: return '#389e0d';
    }
  }};
`;

const AlertIcon = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const Badge = styled.span`
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 2px;
  padding: 1px 8px;
  margin-left: 8px;
  font-size: 12px;
  border: 1px solid #91d5ff;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
  font-size: 14px;
  &:focus {
    border-color: #1890ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionIcon = styled.span`
  display: flex;
  align-items: center;
  color: #1890ff;
`;

// User Admin Page Component
const UserAdminPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [showAssignAIModal, setShowAssignAIModal] = useState(false);
  const [assigningUser, setAssigningUser] = useState(null);
  const [availableAIModels, setAvailableAIModels] = useState([]);
  const [showAIUsageStats, setShowAIUsageStats] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userService.list();
      if (response.success) {
        setUsers(response.users);
        
        // Store the current user ID for highlighting
        if (response.currentUser) {
          setCurrentUserId(response.currentUser);
          
          // Check if current user is admin
          const currentUser = response.users.find(user => user.user_id === response.currentUser);
          if (currentUser && currentUser.role === 'admin') {
            setIsAdmin(true);
          }
        }
      } else {
        setError(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message || 'An error occurred while fetching users');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (newUser) => {
    try {
      setError(null);
      
      // Generate a username from the email address (part before @)
      const username = newUser.email.split('@')[0];
      const userToCreate = {
        ...newUser,
        username
      };
      
      const response = await userService.create(userToCreate);
      if (response.success) {
        // Refresh the user list to get the newly created user
        fetchUsers();
        setShowAddModal(false);
      } else {
        setError('Failed to create user: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error.message || 'An error occurred while creating the user');
    }
  };

  const handleInviteUser = async (userData) => {
    try {
      setError(null);
      setInviteSuccess(false);
      
      const response = await userService.inviteUser(userData);
      if (response.success) {
        // Refresh the user list to get the newly invited user
        fetchUsers();
        setShowInviteModal(false);
        setInviteSuccess(true);
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setInviteSuccess(false);
        }, 5000);
      } else {
        setError('Failed to invite user: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      // Extract the error message from the response if available
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while inviting the user';
      setError(errorMessage);
    }
  };

  const handleEditStart = (idx) => {
    setEditingIndex(idx);
    setEditingUser({ ...users[idx] });
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditingUser(null);
  };

  const handleEditChange = (field, value) => {
    setEditingUser(prev => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async (idx) => {
    try {
      const response = await userService.update(users[idx].user_id, editingUser);
      if (response.success) {
        const updatedUsers = [...users];
        updatedUsers[idx] = { ...updatedUsers[idx], ...editingUser };
        setUsers(updatedUsers);
        setEditingIndex(null);
      } else {
        setError('Failed to update user');
      }
    } catch (error) {
      setError(error.message || 'An error occurred while updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await userService.delete(userId);
      
      if (response.success) {
        setUsers(users.filter(user => user.user_id !== userId));
      } else {
        setError('Failed to delete user');
      }
    } catch (error) {
      setError(error.message || 'An error occurred while deleting the user');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewUser = (user) => {
    setViewingUser(user);
    setShowViewModal(true);
  };

  const handleAssignAIModel = async (user) => {
    try {
      setLoading(true);
      setAssigningUser(user);
      
      // Fetch available AI models
      const modelsData = await getAvailableAIModels();
      
      // Check if we have the expected data structure
      if (modelsData && modelsData.providers) {
        // Create a flattened array of models with provider information
        const allModels = [];
        modelsData.providers.forEach(provider => {
          provider.models.forEach(model => {
            allModels.push({
              id: model.id,
              name: model.name,
              description: model.description || '',
              provider: provider.name
            });
          });
        });
        
        setAvailableAIModels(allModels);
        setShowAssignAIModal(true);
      } else {
        setError('Invalid response format from AI models API');
      }
    } catch (error) {
      console.error('Error fetching AI models:', error);
      setError(error.message || 'An error occurred while fetching AI models');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAIModel = async (userId, aiModel) => {
    try {
      setLoading(true);
      
      const response = await userService.updateUserAIModel(userId, aiModel);
      
      if (response.success) {
        // Update the user in the local state
        const updatedUsers = users.map(user => {
          if (user.user_id === userId) {
            return {
              ...user,
              ai_provider: aiModel.ai_provider,
              ai_model: aiModel.ai_model
            };
          }
          return user;
        });
        
        setUsers(updatedUsers);
        setShowAssignAIModal(false);
        setError(null);
        
        // Show success message
        alert('AI model assigned successfully');
      } else {
        setError('Failed to assign AI model');
      }
    } catch (error) {
      console.error('Error assigning AI model:', error);
      setError(error.message || 'An error occurred while assigning AI model');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const term = searchQuery.toLowerCase();
    return (
      user.username.toLowerCase().includes(term) ||
      (user.email && user.email.toLowerCase().includes(term))
    );
  });

  const paginatedUsers = filteredUsers.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  return (
    <PageContainer>
      <TabsContainer>
        <Tab active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
          <FaUsers /> Users
        </Tab>
        <Tab active={activeTab === 'ai-models'} onClick={() => setActiveTab('ai-models')}>
          <FaBrain /> AI Models
        </Tab>
        <Tab active={activeTab === 'ai-usage'} onClick={() => setActiveTab('ai-usage')}>
          <FaChartBar /> AI Usage
        </Tab>
        <Tab active={activeTab === 'questions'} onClick={() => setActiveTab('questions')}>
          <FaSearch /> Questions
        </Tab>
      </TabsContainer>
      
      {!isAdmin && (
        <Alert type="warning">
          <AlertIcon><FaExclamationCircle /></AlertIcon>
          You don't have administrator privileges. Only admin users can access the user management panel.
        </Alert>
      )}
      
      {activeTab === 'users' && isAdmin ? (
        <>
          <PageHeader>
            <PageTitle>
              <FaUserShield /> User Management
            </PageTitle>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <SearchIcon><FaSearch /></SearchIcon>
            </SearchContainer>
            {/* <Button primary onClick={() => setShowAddModal(true)}>
              <FaPlus /> Add User
            </Button> */}
            <Button primary onClick={() => setShowInviteModal(true)}>
              <FaPlus /> Invite User
            </Button>
          </PageHeader>

          {/* Add User Modal */}
          <AddUserModal 
            show={showAddModal} 
            onClose={() => setShowAddModal(false)} 
            onAdd={handleAddUser} 
          />

          {/* Invite User Modal */}
          <InviteUserModal 
            show={showInviteModal} 
            onClose={() => setShowInviteModal(false)} 
            onInvite={handleInviteUser} 
          />

          {/* Success message for invitation */}
          {inviteSuccess && (
            <Alert type="success">
              <AlertIcon><FaCheck /></AlertIcon>
              Invitation sent successfully! An email has been sent to the user with instructions to set their password and activate their account.
            </Alert>
          )}

          {/* View User Modal */}
          <ViewUserModal
            show={showViewModal}
            onClose={() => setShowViewModal(false)}
            user={viewingUser}
          />

          {/* Assign AI Model Modal */}
          <AssignAIModelModal
            show={showAssignAIModal}
            onClose={() => setShowAssignAIModal(false)}
            user={assigningUser}
            models={availableAIModels}
            onSave={handleSaveAIModel}
          />

          <Card>
            {loading ? (
              <LoadingContainer>
                <FaSync className="spin" />
                <span>Loading users...</span>
              </LoadingContainer>
            ) : error ? (
              <Alert type="error">
                <AlertIcon><FaExclamationCircle /></AlertIcon>
                {error}
              </Alert>
            ) : filteredUsers.length === 0 ? (
              <EmptyState>
                <FaUsers size={48} color="#d9d9d9" />
                <EmptyStateText>
                  {searchQuery ? 'No users match your search' : 'No users found'}
                </EmptyStateText>
              </EmptyState>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeader>Username</TableHeader>
                        <TableHeader>Email</TableHeader>
                        <TableHeader>Phone</TableHeader>
                        <TableHeader>Company</TableHeader>
                        <TableHeader>Role</TableHeader>
                        <TableHeader>AI Model</TableHeader>
                        <TableHeader>Actions</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedUsers.map((user, idx) => {
                        const isEditing = editingIndex === idx;
                        const isCurrentUser = user.user_id === currentUserId;
                        
                        return (
                          <TableRow key={user.user_id} highlighted={isCurrentUser}>
                            <TableCell>
                              {isEditing ? (
                                <Input
                                  value={editingUser.username || ''}
                                  onChange={(e) => handleEditChange('username', e.target.value)}
                                />
                              ) : (
                                <>
                                  {user.username}
                                  {isCurrentUser && (
                                    <Badge>You</Badge>
                                  )}
                                </>
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <Input
                                  type="email"
                                  value={editingUser.email || ''}
                                  onChange={(e) => handleEditChange('email', e.target.value)}
                                />
                              ) : (
                                user.email
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <Input
                                  type="tel"
                                  value={editingUser.phone_number || ''}
                                  onChange={(e) => handleEditChange('phone_number', e.target.value)}
                                />
                              ) : (
                                user.phone_number || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <Input
                                  value={editingUser.company_name || ''}
                                  onChange={(e) => handleEditChange('company_name', e.target.value)}
                                />
                              ) : (
                                user.company_name || '-'
                              )}
                            </TableCell>
                            <TableCell>
                              {isEditing ? (
                                <select
                                  value={editingUser.role || 'user'}
                                  onChange={(e) => handleEditChange('role', e.target.value)}
                                  style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #d9d9d9'
                                  }}
                                >
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                </select>
                              ) : (
                                <RoleBadge role={user.role}>
                                  {user.role === 'admin' ? (
                                    <FaUserShield style={{ marginRight: '4px' }} />
                                  ) : (
                                    <FaUser style={{ marginRight: '4px' }} />
                                  )}
                                  {user.role}
                                </RoleBadge>
                              )}
                            </TableCell>
                            <TableCell>
                              {user.ai_provider && user.ai_model ? (
                                <span>
                                  {user.ai_provider} - {user.ai_model}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <ActionButtons>
                                {isEditing ? (
                                  <>
                                    <ActionButton onClick={() => handleEditSave(idx)} title="Save">
                                      <FaSave />
                                    </ActionButton>
                                    <ActionButton onClick={handleEditCancel} title="Cancel">
                                      <FaTimes />
                                    </ActionButton>
                                  </>
                                ) : (
                                  <>
                                    <ActionButton onClick={() => handleEditStart(idx)} title="Edit user">
                                      <FaEdit />
                                    </ActionButton>
                                    <ActionButton onClick={() => handleViewUser(user)} title="View user">
                                      <FaEye />
                                    </ActionButton>
                                    <ActionButton onClick={() => handleAssignAIModel(user)} title="Assign AI Model">
                                      <FaBrain />
                                    </ActionButton>
                                    {!isCurrentUser && (
                                      <ActionButton 
                                        onClick={() => handleDeleteUser(user.user_id)} 
                                        title="Delete user"
                                        danger
                                      >
                                        <FaTrash />
                                      </ActionButton>
                                    )}
                                  </>
                                )}
                              </ActionButtons>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {totalPages > 1 && (
                  <Pagination>
                    <Button 
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <PaginationInfo>
                      Page {currentPage + 1} of {totalPages}
                    </PaginationInfo>
                    <Button 
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage === totalPages - 1}
                    >
                      Next
                    </Button>
                  </Pagination>
                )}
              </>
            )}
          </Card>
        </>
      ) : activeTab === 'ai-models' ? (
        <AIModelManager />
      ) : activeTab === 'ai-usage' ? (
        <AIUsageStats />
      ) : activeTab === 'questions' ? (
        <QuestionsTab />
      ) : (
        <Card>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <SectionTitle>
              <SectionIcon><FaLock /></SectionIcon>
              Access Restricted
            </SectionTitle>
            <p style={{ marginTop: '20px' }}>
              You need administrator privileges to access this section.
            </p>
          </div>
        </Card>
      )}
    </PageContainer>
  );
};

// Modal for adding a new user
const AddUserModal = ({ show, onClose, onAdd }) => {
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'user' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onAdd(newUser);
      // Reset form
      setNewUser({ email: '', password: '', role: 'user' });
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        width: '400px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>Add New User</h3>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        
        {error && (
          <Alert type="error">
            <FaExclamationCircle style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email</label>
            <Input 
              type="email" 
              name="email" 
              value={newUser.email} 
              onChange={handleChange} 
              required 
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Password</label>
            <Input 
              type="password" 
              name="password" 
              value={newUser.password} 
              onChange={handleChange} 
              required 
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Role</label>
            <select 
              name="role" 
              value={newUser.role} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button primary type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal for inviting a new user
const InviteUserModal = ({ show, onClose, onInvite }) => {
  const [newUser, setNewUser] = useState({ email: '', role: 'user' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Clear error and form when modal is opened/closed
    if (show) {
      setError(null);
    } else {
      // Reset form when modal is closed
      setNewUser({ email: '', role: 'user' });
      setError(null);
    }
  }, [show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onInvite(newUser);
      // Form will be reset when modal is closed on success
    } catch (err) {
      setError(err.message || 'Failed to invite user');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        width: '400px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>Invite New User</h3>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        
        {error && (
          <Alert type="error">
            <FaExclamationCircle style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email</label>
            <Input 
              type="email" 
              name="email" 
              value={newUser.email} 
              onChange={handleChange} 
              required 
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Role</label>
            <select 
              name="role" 
              value={newUser.role} 
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #d9d9d9',
                fontSize: '14px'
              }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button primary type="submit" disabled={loading}>
              {loading ? 'Inviting...' : 'Invite User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal for viewing a user
const ViewUserModal = ({ show, onClose, user }) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        width: '400px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>View User</h3>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Username:</label>
            <span>{user.username}</span>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email:</label>
            <span>{user.email}</span>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Phone:</label>
            <span>{user.phone_number || '-'}</span>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Company:</label>
            <span>{user.company_name || '-'}</span>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Role:</label>
            <RoleBadge role={user.role}>
              {user.role === 'admin' ? (
                <FaUserShield style={{ marginRight: '4px' }} />
              ) : (
                <FaUser style={{ marginRight: '4px' }} />
              )}
              {user.role}
            </RoleBadge>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>AI Model:</label>
            <span>
              {user.ai_provider && user.ai_model ? (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <FaBrain style={{ marginRight: '4px', color: '#1890ff' }} />
                  {user.ai_provider.charAt(0).toUpperCase() + user.ai_provider.slice(1)} - {user.ai_model}
                </span>
              ) : (
                'Not assigned'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAdminPage;

// Modal for assigning AI model to a user
const AssignAIModelModal = ({ show, onClose, user, models, onSave }) => {
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignedModels, setAssignedModels] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [loadingAssignedModels, setLoadingAssignedModels] = useState(false);

  useEffect(() => {
    if (show && user) {
      // Set initial values if user already has AI model assigned
      setSelectedProvider(user.ai_provider || '');
      setSelectedModel(user.ai_model || '');
      setIsDefault(false);
      
      // Load all assigned models for this user
      fetchAssignedModels(user.user_id);
    } else {
      setSelectedProvider('');
      setSelectedModel('');
      setAssignedModels([]);
      setIsDefault(false);
    }
  }, [show, user]);
  
  const fetchAssignedModels = async (userId) => {
    try {
      setLoadingAssignedModels(true);
      const response = await userService.getUserAIModels(userId);
      if (response.success) {
        setAssignedModels(response.models || []);
      } else {
        console.error('Failed to fetch assigned models:', response);
      }
    } catch (error) {
      console.error('Error fetching assigned models:', error);
    } finally {
      setLoadingAssignedModels(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedProvider || !selectedModel) {
      setError('Please select both provider and model');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use the new assignAIModel method instead of updateUserAIModel
      await userService.assignAIModel(user.user_id, {
        ai_provider: selectedProvider,
        ai_model: selectedModel,
        is_default: isDefault
      });
      
      // Refresh the assigned models list
      await fetchAssignedModels(user.user_id);
      
      // Clear the form
      setSelectedProvider('');
      setSelectedModel('');
      setIsDefault(false);
      setError(null);
      
      // Show success message
      alert('AI model assigned successfully');
    } catch (error) {
      setError(error.message || 'Failed to assign AI model');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRemoveModel = async (provider, model) => {
    try {
      setLoading(true);
      await userService.removeAIModel(user.user_id, provider, model);
      
      // Refresh the assigned models list
      await fetchAssignedModels(user.user_id);
      
      // Show success message
      alert('AI model removed successfully');
    } catch (error) {
      setError(error.message || 'Failed to remove AI model');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetDefault = async (provider, model) => {
    try {
      setLoading(true);
      await userService.setDefaultAIModel(user.user_id, provider, model);
      
      // Refresh the assigned models list
      await fetchAssignedModels(user.user_id);
      
      // Show success message
      alert('Default AI model set successfully');
    } catch (error) {
      setError(error.message || 'Failed to set default AI model');
    } finally {
      setLoading(false);
    }
  };

  if (!show || !user) return null;

  // Filter models by selected provider
  const filteredModels = models.filter(model => 
    selectedProvider ? model.provider === selectedProvider : true
  );

  // Get unique providers
  const providers = [...new Set(models.map(model => model.provider))];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '4px',
        width: '600px',
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0 }}>
            <FaBrain style={{ marginRight: '8px', color: '#1890ff' }} />
            Assign AI Models to {user.username}
          </h3>
          <button 
            onClick={onClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
          >
            ×
          </button>
        </div>
        
        {error && (
          <Alert type="error" style={{ marginBottom: '16px' }}>
            <FaExclamationCircle style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}
        
        {/* Assigned Models Section */}
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ marginBottom: '12px' }}>Assigned AI Models</h4>
          {loadingAssignedModels ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
          ) : assignedModels.length === 0 ? (
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'center' }}>
              No AI models assigned yet
            </div>
          ) : (
            <div style={{ 
              border: '1px solid #e8e8e8', 
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Provider</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Model</th>
                    <th style={{ padding: '12px', textAlign: 'center' }}>Default</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedModels.map((model, index) => (
                    <tr key={index} style={{ 
                      borderTop: '1px solid #e8e8e8',
                      backgroundColor: model.is_default ? '#e6f7ff' : 'white'
                    }}>
                      <td style={{ padding: '12px' }}>
                        {model.ai_provider.charAt(0).toUpperCase() + model.ai_provider.slice(1)}
                      </td>
                      <td style={{ padding: '12px' }}>{model.ai_model}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {model.is_default ? (
                          <FaCheck style={{ color: '#52c41a' }} />
                        ) : (
                          <button
                            onClick={() => handleSetDefault(model.ai_provider, model.ai_model)}
                            disabled={loading}
                            style={{
                              background: 'none',
                              border: '1px solid #d9d9d9',
                              borderRadius: '2px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Set Default
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleRemoveModel(model.ai_provider, model.ai_model)}
                          disabled={loading}
                          style={{
                            background: 'none',
                            border: '1px solid #ff4d4f',
                            borderRadius: '2px',
                            padding: '4px 8px',
                            color: '#ff4d4f',
                            cursor: 'pointer'
                          }}
                        >
                          <FaTrash style={{ marginRight: '4px' }} />
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Add New Model Form */}
        <div style={{ marginTop: '24px' }}>
          <h4 style={{ marginBottom: '12px' }}>Add New AI Model</h4>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>AI Provider</label>
              <select
                value={selectedProvider}
                onChange={(e) => {
                  setSelectedProvider(e.target.value);
                  setSelectedModel(''); // Reset model when provider changes
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px'
                }}
              >
                <option value="">Select Provider</option>
                {providers.map((provider, index) => (
                  <option key={index} value={provider}>
                    {provider.charAt(0).toUpperCase() + provider.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>AI Model</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={!selectedProvider}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px'
                }}
              >
                <option value="">Select Model</option>
                {filteredModels.map((model, index) => (
                  <option key={index} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Set as default model
              </label>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '2px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedProvider || !selectedModel}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #1890ff',
                  borderRadius: '2px',
                  backgroundColor: '#1890ff',
                  color: 'white',
                  cursor: 'pointer',
                  opacity: loading || !selectedProvider || !selectedModel ? 0.7 : 1
                }}
              >
                {loading ? 'Assigning...' : 'Assign Model'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
